import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import type {
  UseFormRegister,
  UseFormSetValue,
  UseFormWatch,
  UseFormTrigger,
  FieldErrors,
} from 'react-hook-form';
import { createMedicalLeaveFormSchema, type CreateMedicalLeaveFormValues } from '@/schemas/absence';
import { absenceService } from '@/api/domains/absence/absence.service';
import { ABSENCE_SCOPE_LIST_REFRESH_EVENT } from '@/api/domains/absence/absence.types';
import type {
  LC_MedicalLeaveTypeItemOut,
  LC_AbsenceWorkdayTypeItemOut,
  LC_EmployeeForAbsenceItemOut,
} from '@/api/domains/absence/absence.types';
import { useUI } from '@/hooks/ui';
import { useSidePanelLoading } from '@/hooks/ui/useSidePanelLoading';
import { useDebounce } from '@/hooks/ui/useDebounce';
import type { SelectorOption } from '@/components/ui/inputs/selector';
import { normalizeNumericSelectValue } from '@/utils/formHelpers';
import { formatDateToYYYYMMDD, injectValidationErrors } from '@/utils';


const PAGE_SIZE = 20;
const CONFLICT_DATE_OVERLAP_CODE = 'CONFLICT_DATE_OVERLAP';

export interface UseCreateMedicalLeaveResult {
  // Form
  form: {
    register: UseFormRegister<CreateMedicalLeaveFormValues>;
    setValue: UseFormSetValue<CreateMedicalLeaveFormValues>;
    watch: UseFormWatch<CreateMedicalLeaveFormValues>;
    errors: FieldErrors<CreateMedicalLeaveFormValues>;
    trigger: UseFormTrigger<CreateMedicalLeaveFormValues>;
  };
  isSubmitted: boolean;
  isSubmitting: boolean;
  handleSubmit: (event?: React.FormEvent<HTMLFormElement>) => void;

  // Employee selector
  employeeOptions: SelectorOption[];
  employeeBlockedIds: Set<number>;
  isLoadingEmployees: boolean;
  isLoadingMoreEmployees: boolean;
  employeeHasMore: boolean;
  handleEmployeeSearch: (search: string) => void;
  handleEmployeeChange: (value: string | string[]) => void;
  loadMoreEmployees: () => Promise<void>;

  // Leave types
  leaveTypeOptions: SelectorOption[];
  isLoadingLeaveTypes: boolean;

  // Workday types
  workdayTypeOptions: SelectorOption[];
  isLoadingWorkdayTypes: boolean;
}

export function useCreateMedicalLeave(): UseCreateMedicalLeaveResult {
  const { t: translate } = useTranslation('absence');
  const { showApiError, showSuccess, showNotification, closeSidePanel } = useUI();
  const { setLoading } = useSidePanelLoading();

  // ── Leave types ────────────────────────────────────────────────────────────
  // Nota: no se usa AbortController aquí — es un catálogo pequeño de una sola pasada;
  // abortarlo en el cleanup generaría TaskCanceledException en el backend durante los
  // doble-montajes de React.StrictMode en desarrollo (ver useCatalogLoader.ts).
  const [leaveTypes, setLeaveTypes] = useState<LC_MedicalLeaveTypeItemOut[]>([]);
  const [isLoadingLeaveTypes, setIsLoadingLeaveTypes] = useState(false);

  const loadLeaveTypes = useCallback(async () => {
    setIsLoadingLeaveTypes(true);
    try {
      const response = await absenceService.getMedicalLeaveTypes();
      if (response.success && response.data) {
        setLeaveTypes(response.data);
      } else {
        showApiError(response, translate('medicalLeave.errors.loadLeaveTypesFailed'));
      }
    } finally {
      setIsLoadingLeaveTypes(false);
    }
  }, [showApiError, translate]);

  useEffect(() => {
    void loadLeaveTypes();
  }, [loadLeaveTypes]);

  const leaveTypeOptions = useMemo<SelectorOption[]>(
    () => leaveTypes.map((type) => ({ value: String(type.id), label: type.name })),
    [leaveTypes],
  );

  // ── Workday types ──────────────────────────────────────────────────────────
  // Mismo motivo que loadLeaveTypes: catálogo estático, sin AbortController.
  const [workdayTypes, setWorkdayTypes] = useState<LC_AbsenceWorkdayTypeItemOut[]>([]);
  const [isLoadingWorkdayTypes, setIsLoadingWorkdayTypes] = useState(false);

  const loadWorkdayTypes = useCallback(async () => {
    setIsLoadingWorkdayTypes(true);
    try {
      const response = await absenceService.getAbsenceWorkdayTypes();
      if (response.success && response.data) {
        setWorkdayTypes(response.data);
      } else {
        showApiError(response, translate('medicalLeave.errors.loadWorkdayTypesFailed'));
      }
    } finally {
      setIsLoadingWorkdayTypes(false);
    }
  }, [showApiError, translate]);

  useEffect(() => {
    void loadWorkdayTypes();
  }, [loadWorkdayTypes]);

  const workdayTypeOptions = useMemo<SelectorOption[]>(
    () => workdayTypes.map((type) => ({ value: type.code, label: type.label })),
    [workdayTypes],
  );

  // ── Employee selector ──────────────────────────────────────────────────────
  const [employees, setEmployees] = useState<LC_EmployeeForAbsenceItemOut[]>([]);
  const [employeePage, setEmployeePage] = useState(1);
  const [employeeHasMore, setEmployeeHasMore] = useState(false);
  const [employeeSearchText, setEmployeeSearchText] = useState('');
  const [isLoadingEmployees, setIsLoadingEmployees] = useState(false);
  const [isLoadingMoreEmployees, setIsLoadingMoreEmployees] = useState(false);
  const selectedEmployeeRef = useRef<LC_EmployeeForAbsenceItemOut | null>(null);

  const loadEmployees = useCallback(
    async (page: number, search: string, append: boolean = false) => {
      if (append) {
        setIsLoadingMoreEmployees(true);
      } else {
        setIsLoadingEmployees(true);
      }
      try {
        const response = await absenceService.getEmployeesForAbsence({
          page,
          pageSize: PAGE_SIZE,
          searchText: search || undefined,
        });

        if (response.success && response.data) {
          const newItems = response.data.data ?? [];
          setEmployees((previous) => {
            let result = append ? [...previous, ...newItems] : newItems;
            const selected = selectedEmployeeRef.current;
            if (selected && !result.some((employee) => employee.id === selected.id)) {
              result = [selected, ...result];
            }
            return result;
          });
          setEmployeeHasMore(response.data.pagination.hasNextPage);
          setEmployeePage(page);
        } else {
          showApiError(response, translate('medicalLeave.errors.loadEmployeesFailed'));
        }
      } finally {
        if (append) {
          setIsLoadingMoreEmployees(false);
        } else {
          setIsLoadingEmployees(false);
        }
      }
    },
    [showApiError, translate],
  );

  useEffect(() => {
    void loadEmployees(1, '');
  }, [loadEmployees]);

  const loadMoreEmployees = useCallback(async () => {
    if (!isLoadingMoreEmployees && employeeHasMore) {
      await loadEmployees(employeePage + 1, employeeSearchText, true);
    }
  }, [isLoadingMoreEmployees, employeeHasMore, employeePage, employeeSearchText, loadEmployees]);

  const handleEmployeeSearch = useCallback(
    (search: string) => {
      setEmployeeSearchText(search);
      void loadEmployees(1, search);
    },
    [loadEmployees],
  );

  const employeeOptions = useMemo<SelectorOption[]>(
    () =>
      employees.map((employee) => ({
        value: String(employee.id),
        label: employee.fullName
          ? `${employee.fullName}${employee.employeeTaxIdentificationNumber ? ` (${employee.employeeTaxIdentificationNumber})` : ''}`
          : String(employee.id),
        supportingText: employee.companyName || undefined,
        disabled: employee.isBlocked,
        disabledTooltip: employee.isBlocked
          ? translate('medicalLeave.form.blockedEmployeeTooltip')
          : undefined,
      })),
    [employees, translate],
  );

  const employeeBlockedIds = useMemo<Set<number>>(
    () => new Set(
      employees
        .filter((employee) => employee.isBlocked)
        .map((employee) => employee.id),
    ),
    [employees],
  );

  // ── Loading state ──────────────────────────────────────────────────────────
  const initialLoadDoneRef = useRef(false);
  const areCatalogsLoaded = useMemo(
    () => !isLoadingEmployees && !isLoadingLeaveTypes && !isLoadingWorkdayTypes,
    [isLoadingEmployees, isLoadingLeaveTypes, isLoadingWorkdayTypes],
  );

  useEffect(() => {
    if (!initialLoadDoneRef.current) {
      setLoading(!areCatalogsLoaded);
      if (areCatalogsLoaded) {
        initialLoadDoneRef.current = true;
      }
    }
  }, [areCatalogsLoaded, setLoading]);

  // ── Form ───────────────────────────────────────────────────────────────────
  const [isSubmitting, setIsSubmitting] = useState(false);
  const overlapErrorBaselineRef = useRef<{ startDate: string; endDate: string } | null>(null);

  const {
    register,
    setValue,
    watch,
    trigger,
    setError,
    clearErrors,
    handleSubmit: rhfHandleSubmit,
    formState,
  } = useForm<CreateMedicalLeaveFormValues>({
    resolver: zodResolver(createMedicalLeaveFormSchema),
    mode: 'onSubmit',
    defaultValues: {
      employeeTargetId: null,
      leaveTypeId: null,
      startDate: '',
      endDate: '',
      folio: '',
      workdayType: undefined as unknown as string,
      dayCount: null,
    },
  });

  // Fecha de término = Fecha de inicio + Cantidad de días, recalculada automáticamente.
  const startDateForEndDate = watch('startDate');
  const dayCountForEndDate = watch('dayCount');

  useEffect(() => {
    const dayCount = Number(dayCountForEndDate);
    if (!startDateForEndDate || !Number.isInteger(dayCount) || dayCount <= 0) return;

    const [year, month, day] = startDateForEndDate.split('-').map(Number);
    const computedEndDate = new Date(year, month - 1, day);
    computedEndDate.setDate(computedEndDate.getDate() + dayCount - 1);

    setValue('endDate', formatDateToYYYYMMDD(computedEndDate), {
      shouldDirty: true,
      shouldValidate: true,
    });
  }, [startDateForEndDate, dayCountForEndDate, setValue]);

  // Fecha de retorno = próximo día hábil posterior a Fecha de término, calculada por el backend.
  const returnDateRequestIdRef = useRef(0);

  const calculateReturnDate = useCallback(
    async (employeeTargetId: number, endDate: string, workdayType: string) => {
      const requestId = ++returnDateRequestIdRef.current;
      const response = await absenceService.calculateReturnDate({
        employeeTargetId,
        endDate,
        workdayType,
      });
      if (requestId !== returnDateRequestIdRef.current) return; // respuesta obsoleta, ignorar
      if (response.success && response.data) {
        setValue('returnDate', response.data.returnDate, { shouldDirty: true });
      } else {
        showApiError(response, translate('medicalLeave.errors.calculateReturnDateFailed'));
      }
    },
    [setValue, showApiError, translate],
  );

  const employeeTargetIdForReturnDate = watch('employeeTargetId');
  const endDateForReturnDate = watch('endDate');
  const workdayTypeForReturnDate = watch('workdayType');
  const debouncedEndDateForReturnDate = useDebounce(endDateForReturnDate, 400);

  useEffect(() => {
    if (
      !employeeTargetIdForReturnDate ||
      !debouncedEndDateForReturnDate ||
      !workdayTypeForReturnDate
    ) {
      setValue('returnDate', '');
      return;
    }
    void calculateReturnDate(
      employeeTargetIdForReturnDate as number,
      debouncedEndDateForReturnDate,
      workdayTypeForReturnDate,
    );
  }, [
    employeeTargetIdForReturnDate,
    debouncedEndDateForReturnDate,
    workdayTypeForReturnDate,
    calculateReturnDate,
    setValue,
  ]);

  // Si el usuario cambia las fechas tras un error de solapamiento, se limpia para evitar confusión.
  useEffect(() => {
    const baseline = overlapErrorBaselineRef.current;
    if (!baseline) return;
    if (baseline.startDate !== startDateForEndDate || baseline.endDate !== endDateForReturnDate) {
      overlapErrorBaselineRef.current = null;
      clearErrors(['startDate', 'endDate']);
    }
  }, [startDateForEndDate, endDateForReturnDate, clearErrors]);

  const onValid = useCallback(
    async (data: CreateMedicalLeaveFormValues) => {
      setIsSubmitting(true);
      try {
        const response = await absenceService.createMedicalLeave({
          employeeTargetId: data.employeeTargetId as number,
          leaveTypeId: data.leaveTypeId as number,
          startDate: data.startDate,
          endDate: data.endDate,
          returnDate: data.returnDate || undefined,
          folio: data.folio,
          workdayType: data.workdayType,
          dayCount: data.dayCount as number,
        });

        if (response.success) {
          showSuccess(translate('medicalLeave.messages.createSuccess'));
          window.dispatchEvent(new CustomEvent(ABSENCE_SCOPE_LIST_REFRESH_EVENT));
          closeSidePanel?.();
        } else if (response.error?.code === CONFLICT_DATE_OVERLAP_CODE) {
          overlapErrorBaselineRef.current = { startDate: data.startDate, endDate: data.endDate };
          setError('startDate', { message: 'absence:medicalLeave.errors.dateOverlap' });
          setError('endDate', { message: 'absence:medicalLeave.errors.dateOverlap' });
          showNotification('error', translate('medicalLeave.errors.dateOverlap'));
        } else if (response.error?.validationErrors && response.error.validationErrors.length > 0) {
          injectValidationErrors(response.error.validationErrors, { setError });
        } else {
          showApiError(response, translate('medicalLeave.errors.createFailed'));
        }
      } finally {
        setIsSubmitting(false);
      }
    },
    [closeSidePanel, setError, showApiError, showNotification, showSuccess, translate],
  );

  const handleEmployeeChange = useCallback(
    (value: string | string[]) => {
      const stringValue = Array.isArray(value) ? (value[0] ?? '') : value;
      const numericId = normalizeNumericSelectValue(stringValue);
      if (numericId !== undefined) {
        setValue('employeeTargetId', numericId as number, { shouldDirty: true });
        const found = employees.find((employee) => employee.id === numericId);
        if (found) {
          selectedEmployeeRef.current = found;
        }
      }
    },
    [employees, setValue],
  );

  const handleSubmit = useCallback(
    (event?: React.FormEvent<HTMLFormElement>) => {
      if (event && typeof event.preventDefault === 'function') {
        event.preventDefault();
        event.stopPropagation();
      }
      const wrappedValid = (data: CreateMedicalLeaveFormValues) => { void onValid(data); };
      const wrappedInvalid = () => {};
      const runner = rhfHandleSubmit(wrappedValid, wrappedInvalid);
      void runner(event);
    },
    [rhfHandleSubmit, onValid],
  );

  return {
    form: {
      register,
      setValue,
      watch,
      errors: formState.errors,
      trigger,
    },
    isSubmitted: formState.isSubmitted,
    isSubmitting,
    handleSubmit,
    employeeOptions,
    employeeBlockedIds,
    isLoadingEmployees,
    isLoadingMoreEmployees,
    employeeHasMore,
    handleEmployeeSearch,
    handleEmployeeChange,
    loadMoreEmployees,
    leaveTypeOptions,
    isLoadingLeaveTypes,
    workdayTypeOptions,
    isLoadingWorkdayTypes,
  };
}

export default useCreateMedicalLeave;
